import { createContext, useContext, useState, useEffect } from 'react';
import api, { setStoredToken } from '../api/axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // On mount, try to validate the session by calling a lightweight authenticated endpoint.
    // If the HttpOnly access_token cookie is valid, the request succeeds and we get the user.
    // If not, we clear state and show the login page.
    const validateSession = async () => {
      try {
        const response = await api.get('auth/me');
        if (response.data?.data) {
          setUser(response.data.data);
        }
      } catch {
        // No valid session — user will see login page
        setStoredToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    validateSession();
  }, []);

  const login = (userData) => {
    // Token is set as HttpOnly cookie by the backend — nothing to store in JS
    setUser(userData);
  };

  const logout = async () => {
    try {
      await api.post('auth/logout');
    } catch {
      // Even if the API call fails, clear local state
    }
    setStoredToken(null);
    // Clear the impersonation banner marker too. Only the banner's "Exit Impersonation"
    // button removed it before; a plain logout while impersonating would otherwise leave a
    // stale "IMPERSONATION MODE · Initiated by …" banner for the next session on this device.
    try { localStorage.removeItem('impersonator'); } catch { /* storage unavailable */ }
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);
